import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { Observable, forkJoin, map, of, switchMap, take, tap } from 'rxjs';
import { defaultColorLabels } from '../configs/default-color-labels';
import { extraParamsForCreateGalleryFromPreset } from '../consts';
import { assetStatusOrder } from '../gallery/pipes/asset-status-order.pipe';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { IGNORE_AUTH_ERRORS, TENANT_CONTEXT } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { SubscriptionService } from './subscription.service';
import { ViewGalleryHierarchyService } from './view-gallery-hierarchy.service';

@Injectable({
  providedIn: 'root',
})
export class GalleriesCommonService {
  private galleryPasswordService = inject(GalleryPasswordService);
  private state = inject(StateService);
  private config = inject(PICFLOW_CONFIG);
  private galleryHierarchyService = inject(ViewGalleryHierarchyService);
  private observableLoader = inject(ObservableDataLoaderService);
  private subscriptionService = inject(SubscriptionService);
  private http = inject(HttpClient);
  public galleryPathIsId: Set<string> = new Set();

  public onGetNewGalleries(galleries: GalleryDto[]): Observable<GalleryDto[]> {
    if (!galleries) return of(galleries);
    const tenantIds: Set<string> = new Set();

    galleries.forEach(gallery => {
      // galleryPathIsId being set here
      this.galleryPathIsId.add(gallery.id); // what is this?
      if (gallery.unseen_activities !== undefined && !this.state.unseenActivityCountMap.has(gallery.id)) {
        // setting unseen activities for this gallery
        this.state.unseenActivityCountMap.set(gallery.id, gallery.unseen_activities);
        delete gallery.unseen_activities;
      }

      const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
      if (tenantId) tenantIds.add(tenantId);

      this.fixGallery(gallery);

      if (gallery.parent_gallery_id) {
        this.galleryHierarchyService.add(gallery.parent_gallery_id, [gallery.id], [gallery.path]);
      }
    });

    if (!galleries.filter(gallery => !gallery.tenant_product_name).length) return of(galleries);

    return forkJoin([
      this.subscriptionService.getMainPrice().pipe(take(1)),
      this.state.activeTenant$.pipe(take(1)),
    ]).pipe(
      switchMap(([mainPrice, activeTenant]) => {
        // set tenant object and plan name for galleries
        return forkJoinSafe(
          Array.from(tenantIds).map(tenantId => {
            if (typeof tenantId !== 'string') {
              console.error('tenantId in onGetNewGalleries', tenantId);
              // eslint-disable-next-line no-debugger
              debugger;
            }
            const tenantGaleries = galleries.filter(
              g => ((g.tenant as TenantDto)?.id || (g.tenant as string)) === tenantId
            );

            const gallery =
              tenantGaleries.find(g => !g.password_enabled && !g.section && g.public_access !== 'no_access') ||
              tenantGaleries.find(g => !g.password_enabled && g.public_access !== 'no_access') ||
              tenantGaleries.find(g => !g.password_enabled) ||
              tenantGaleries[0];

            // tenant members
            if (activeTenant?.id === tenantId && mainPrice?.product) {
              return of({
                id: tenantId,
                planName: `Picflow ${mainPrice.product.name}`,
                tenant: activeTenant,
              });
            }

            // gallery members
            if ((gallery.tenant as TenantDto)?.id && gallery.tenant_product_name) {
              return of({
                id: tenantId,
                planName: gallery.tenant_product_name,
                tenant: gallery.tenant as TenantDto,
              });
            }

            let addedPasswordFromPath = false;
            let addedPasswordFromId = false;

            // it is a bit funny that we get the password, this should probably be removed at some point, but for now we need it, otherwise we can't fetch the tenant info
            if (gallery.password_enabled) {
              if (!this.galleryPasswordService.getPassword(gallery.id)) {
                addedPasswordFromId = true;
                this.galleryPasswordService.setPassword(gallery.id, gallery.password);
              }
              if (!this.galleryPasswordService.getPassword(gallery.path)) {
                addedPasswordFromPath = true;
                this.galleryPasswordService.setPassword(gallery.path, gallery.password);
              }
            }

            return this.getTenantInfoFromGallery(gallery.id, tenantId).pipe(
              take(1),
              tap(() => {
                if (addedPasswordFromId) {
                  this.galleryPasswordService.removePassword(gallery.id);
                }
                if (addedPasswordFromPath) {
                  this.galleryPasswordService.removePassword(gallery.path);
                }
              })
            );
          })
        ).pipe(
          map(tenantInfo => {
            galleries.forEach(gallery => {
              const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
              const info = tenantInfo.find(d => d.id === tenantId);

              if (!info) {
                console.log('tenantInfo', tenantInfo);
                console.error('No tenant info found for gallery', gallery, tenantId);
              }

              gallery.tenant_product_name = info.planName;
              gallery.tenant = info.tenant;
            });
            return galleries;
          })
        );
      })
    );
  }

  /**
   * This function fixes galleries that were created without any contents (params: {})
   */
  fixGallery(gallery: GalleryDto) {
    if (!gallery.cover.overlay_color) {
      // Fix for galleries created without any contents in cover:
      gallery.cover = extraParamsForCreateGalleryFromPreset.cover;
    }
    if (gallery.enabled_asset_statuses === null) {
      gallery.enabled_asset_statuses = assetStatusOrder;
    }
    if (gallery.enabled_feedback_options === null) {
      gallery.enabled_feedback_options = ['favourite', 'comment', 'annotation', 'status'];
    }
    if (gallery.color_labels === null) {
      gallery.color_labels = defaultColorLabels;
    }
  }

  public getTenantInfoFromGallery(galleryId: string, tenantId: string) {
    if (typeof tenantId !== 'string') {
      console.error('tenantId', tenantId);
      // eslint-disable-next-line no-debugger
      debugger;
    }
    return this.observableLoader.get('gallery-tenant-info_' + tenantId, () => {
      const url = addGalleryPassword(
        `${this.config.apiEndpoint}/galleries/${galleryId}?include[]=tenant_product_name&expand[]=tenant`,
        this.galleryPasswordService.getPassword(galleryId)
      );
      return this.http
        .get<GalleryDto>(url, {
          context: new HttpContext().set(IGNORE_AUTH_ERRORS, true).set(TENANT_CONTEXT, tenantId),
        })
        .pipe(
          map(gallery => {
            return {
              id: tenantId,
              planName: gallery.tenant_product_name,
              tenant: gallery.tenant as TenantDto,
            };
          })
        );
    });
  }
}
